import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, Avatar, Switch, IconButton, Divider, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Article } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { NotificationsActive, ExpandMore, ExpandLess } from '@material-ui/icons';
import { useSwitchStyles } from '../../../hooks/useSwitchStyles';
import { NOTES_PANEL } from '../../../helpers/constants';

const styles = makeStyles(() => ({
  notesTitle: {
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
    marginLeft: '15px',
    marginTop: '6px',
  },
  notesSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    direction: 'row',
  },
  totalNotesLabel: {
    background: 'rgba(159, 164, 171, 0.32)',
    color: '#6B7280',
  },
  totalNotesLabelDark: {
    background: 'rgba(159, 164, 171, 0.32);',
    color: '#E9E9E9',
  },
  newNotesLabel: {
    background: 'rgba(156, 39, 176, 0.32)',
    color: '#9C27B0',
  },
  newNotesLabelDark: {
    background: 'rgba(156, 39, 176, 0.32)',
    color: '#E1BEE7',
  },
  notesLabel: {
    borderRadius: '34px',
    display: 'inline-flex',
    padding: '3px 6px',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: 'normal',
    margin: '5px 0px',
  },
  notesNotificationIcon: {
    fontSize: '14px',
    margin: '0px 2px',
    color: '#5E176A',
  },
  notesNotificationIconDark: {
    fontSize: '14px',
    margin: '0px 2px',
    color: '#BA68C8',
  },
  fadeOut: {
    opacity: 0,
    transition: 'opacity 1s',
  },
  fadeIn: {
    opacity: 1,
    transition: 'opacity 5s',
  },
  switchPinLabel: {
    fontSize: '14px',
    marginLeft: '5px',
  },
  divider: {
    background: '#9c27b0',
    width: '95%',
    marginTop: 8,
  },
  switchItem: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 8,
    paddingLeft: '16px',
  },
  expandButton: {
    fontSize: '30px',
  },
}));

const NotesHeader = props => {
  const theme = useTheme();
  const switchTheme = useSwitchStyles();
  const {
    newNotes,
    totalNotes,
    setIsChipsClicked,
    setIsExpanded,
    handleAccordionClick,
    isExpanded,
    showOnlyPinned,
    handlePinnedNotes,
    notesPanel,
    setNotesPanel,
  } = props;
  const classes = styles();

  const handleNewClick = event => {
    if (event) {
      event.stopPropagation();
    }
    setIsChipsClicked(true);
    setIsExpanded(true);
  };

  return (
    <Grid container>
      <Grid container style={{ paddingLeft: 16, paddingRight: 16 }}>
        <Grid item xs={2} xl={1}>
          <Avatar aria-label="Notes" style={{ background: '#7D1F8D' }}>
            <Article style={{ color: 'white' }} />
          </Avatar>
        </Grid>
        <Grid item xs={10} xl={3}>
          <Typography className={classes.notesTitle}>Notes</Typography>
        </Grid>
        <Grid item xs={5} xl={4} className={newNotes.length ? classes.fadeIn : classes.fadeOut}>
          <span
            aria-hidden="true"
            className={`${classes.notesLabel}
                ${theme.palette.type === 'light' ? classes.newNotesLabel : classes.newNotesLabelDark}`}
            onClick={handleNewClick}
          >
            <NotificationsActive
              className={`
                  ${
                    theme.palette.type === 'light' ? classes.notesNotificationIcon : classes.notesNotificationIconDark
                  }`}
            />
            {`${newNotes.length} New Notes`}
          </span>
        </Grid>
        <Grid item xs={5} xl={3}>
          <span
            className={`${classes.notesLabel}
                ${theme.palette.type === 'light' ? classes.totalNotesLabel : classes.totalNotesLabelDark}`}
          >
            {`${totalNotes} Total Notes`}
          </span>
        </Grid>
        <Grid item xs={2} xl={1} style={{ marginTop: -12 }}>
          <IconButton color="primary" onClick={handleAccordionClick}>
            {isExpanded ? (
              <ExpandLess className={classes.expandButton} />
            ) : (
              <ExpandMore className={classes.expandButton} />
            )}
          </IconButton>
        </Grid>
      </Grid>
      <Divider className={classes.divider} variant="middle" />
      <Grid item xs={12}>
        <Grid container>
          {notesPanel === NOTES_PANEL.NOTES ? (
            <Grid item xs={12} className={classes.switchItem}>
              <Switch
                checked={showOnlyPinned}
                color="primary"
                onChange={handlePinnedNotes}
                classes={{
                  root: switchTheme.root,
                  switchBase: switchTheme.switchBase,
                  thumb: switchTheme.thumb,
                  track: switchTheme.track,
                  checked: switchTheme.checked,
                }}
              />
              <Typography className={classes.switchPinLabel}>Show Only Pinned Notes</Typography>
            </Grid>
          ) : (
            <Grid item xs={12} className={classes.switchItem}>
              <Button
                variant="text"
                style={{
                  textTransform: 'capitalize',
                  color: theme.palette.type === 'dark' ? '#BA68C8' : '#9C27B0',
                }}
                onClick={() => {
                  setNotesPanel(NOTES_PANEL.NOTES);
                }}
              >
                <ArrowBackIcon />
                Go Back
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

NotesHeader.propTypes = {
  totalNotes: PropTypes.number.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  setIsChipsClicked: PropTypes.func.isRequired,
  handleAccordionClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  newNotes: PropTypes.array.isRequired,
  showOnlyPinned: PropTypes.bool.isRequired,
  handlePinnedNotes: PropTypes.func.isRequired,
  notesPanel: PropTypes.string.isRequired,
  setNotesPanel: PropTypes.func.isRequired,
};

export default NotesHeader;
